/**
 * HeatmapGrid.jsx — Live heatmap scoring grid.
 * Columns = recently scored docs (up to 30).
 * Rows    = questions (up to 96).
 * Scores 0-3 render as colour-coded cells that populate live via SSE.
 * Includes a score-distribution panel (count of 0/1/2/3 across visible cells).
 */

import { useMemo, useState } from "react";

const SCORE_CLASS = ["hm-score-0", "hm-score-1", "hm-score-2", "hm-score-3"];
const SCORE_LABELS = ["None", "Weak", "Partial", "Strong"];
const SCORE_COLORS = ["#9CA3AF", "#60A5FA", "#34D399", "#DB2777"];

export default function HeatmapGrid({ docs = [], questions = [], scores = {}, totalScored = 0, totalDocs = 0, onRefresh }) {
  const [refreshing, setRefreshing] = useState(false);

  const visibleDocs = docs.slice(0, 30);
  const visibleQs   = questions.slice(0, 96);

  // Compute distribution + fill stats across all visible cells
  const { dist, filledCells, totalCells } = useMemo(() => {
    const dist = [0, 0, 0, 0];
    let filled = 0;
    for (const doc of visibleDocs) {
      const docScores = scores[doc.doc_no] || {};
      for (const q of visibleQs) {
        const val = docScores[q];
        if (val !== undefined) {
          filled++;
          const s = Math.min(3, Math.max(0, parseInt(val, 10) || 0));
          dist[s]++;
        }
      }
    }
    return { dist, filledCells: filled, totalCells: visibleDocs.length * visibleQs.length };
  }, [visibleDocs, visibleQs, scores]);

  const fillPct = totalCells > 0 ? Math.round(filledCells / totalCells * 100) : 0;

  function handleRefresh() {
    if (!onRefresh || refreshing) return;
    setRefreshing(true);
    onRefresh();
    setTimeout(() => setRefreshing(false), 1500);
  }

  const RefreshBtn = () => (
    <button
      onClick={handleRefresh}
      disabled={refreshing}
      style={{
        marginLeft: 8,
        padding: "3px 10px",
        fontSize: 11,
        fontWeight: 600,
        border: "1px solid var(--divider-color)",
        borderRadius: 5,
        background: "var(--surface-secondary)",
        color: "var(--text-secondary)",
        cursor: refreshing ? "default" : "pointer",
        opacity: refreshing ? 0.6 : 1,
      }}
    >
      {refreshing ? "Refreshing…" : "↻ Refresh"}
    </button>
  );

  if (docs.length === 0 || questions.length === 0) {
    const hasScore = totalScored > 0;
    const missingWhat = docs.length === 0 ? "doc list" : "question list";
    return (
      <section className="hm-section">
        <div className="hm-section-header">
          <h2 className="hm-section-title">Heatmap Score Grid</h2>
          <span className="hm-section-badge">{totalScored} / {totalDocs}</span>
          {hasScore && <RefreshBtn />}
        </div>
        {hasScore ? (
          <div className="hm-empty" style={{ flexDirection: "column", gap: 8 }}>
            <div className="hm-empty__icon">🔥</div>
            <div>{totalScored} docs scored — {missingWhat} still loading, click Refresh</div>
          </div>
        ) : (
          <div className="hm-empty">
            <div className="hm-empty__icon">🔥</div>
            Score cells populate here in real time as documents are scored.
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="hm-section">
      <div className="hm-section-header">
        <h2 className="hm-section-title">Heatmap Score Grid — Live Preview</h2>
        <span className="hm-section-badge">
          {totalScored.toLocaleString()} / {totalDocs.toLocaleString()} docs scored
        </span>
        <RefreshBtn />
        <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-tertiary)" }}>
          {visibleDocs.length} docs × {visibleQs.length} questions · {fillPct}% populated
        </span>
      </div>

      {/* ── Score distribution panel ──────────────────────────────── */}
      <ScoreDistribution dist={dist} filledCells={filledCells} />

      {/* ── Score legend ─────────────────────────────────────────── */}
      <ScoreLegend />

      {/* ── Grid ─────────────────────────────────────────────────── */}
      <div className="hm-grid-wrap">
        <table className="hm-grid">
          <thead>
            <tr>
              <th className="row-header">Question</th>
              {visibleDocs.map((doc) => (
                <th key={doc.doc_no} title={doc.title}>
                  {doc.title ? truncate(doc.title, 10) : `#${doc.doc_no}`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleQs.map((q, qi) => (
              <tr key={qi}>
                <td className="row-label" title={q}>{truncate(q, 50)}</td>
                {visibleDocs.map((doc) => {
                  const val = (scores[doc.doc_no] || {})[q];
                  if (val === undefined) {
                    return <td key={doc.doc_no} className="hm-score-empty">·</td>;
                  }
                  const score = Math.min(3, Math.max(0, parseInt(val, 10) || 0));
                  return (
                    <td key={doc.doc_no} className={`${SCORE_CLASS[score]} hm-score-anim`} title={`Score: ${score} — ${SCORE_LABELS[score]}`}>
                      {score}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ── Score Distribution Panel ──────────────────────────────────────

function ScoreDistribution({ dist, filledCells }) {
  if (!filledCells) return null;
  return (
    <div style={{
      display: "flex",
      gap: 0,
      borderBottom: "1px solid var(--divider-color-light)",
      background: "var(--surface-secondary)",
    }}>
      {dist.map((count, i) => {
        const pct = filledCells > 0 ? (count / filledCells * 100).toFixed(1) : "0.0";
        const barW = filledCells > 0 ? count / filledCells * 100 : 0;
        return (
          <div key={i} style={{
            flex: 1,
            padding: "10px 16px",
            borderRight: i < 3 ? "1px solid var(--divider-color-light)" : "none",
            position: "relative",
            overflow: "hidden",
          }}>
            {/* background bar */}
            <div style={{
              position: "absolute", left: 0, bottom: 0, height: 3,
              width: `${barW}%`,
              background: SCORE_COLORS[i],
              transition: "width 0.4s ease",
            }} />
            <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
              <span style={{
                fontSize: 22,
                fontWeight: 700,
                color: SCORE_COLORS[i],
                lineHeight: 1,
              }}>
                {count.toLocaleString()}
              </span>
              <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
                {pct}%
              </span>
            </div>
            <div style={{ fontSize: 11, marginTop: 2, color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{
                display: "inline-block", width: 10, height: 10,
                borderRadius: 2, background: SCORE_COLORS[i],
              }} />
              Score {i} — {SCORE_LABELS[i]}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ScoreLegend() {
  return (
    <div style={{
      display: "flex",
      gap: 12,
      padding: "8px 20px",
      borderBottom: "1px solid var(--divider-color-faint)",
      fontSize: 11,
      alignItems: "center",
    }}>
      <span style={{ color: "var(--text-tertiary)" }}>Legend:</span>
      {SCORE_LABELS.map((label, i) => (
        <span key={i} style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span className={SCORE_CLASS[i]} style={{
            display: "inline-block", width: 18, height: 18,
            borderRadius: 3, textAlign: "center",
            fontSize: 11, lineHeight: "18px", fontWeight: 700,
          }}>
            {i}
          </span>
          <span style={{ color: "var(--text-secondary)" }}>{label}</span>
        </span>
      ))}
    </div>
  );
}

function truncate(str, n) {
  if (!str) return "";
  return str.length > n ? str.slice(0, n) + "…" : str;
}
